'use client';

import { FC, useEffect, useRef } from 'react';
import { Content, isFilled } from '@prismicio/client';
import { SliceComponentProps, PrismicRichText } from '@prismicio/react';
import { PrismicNextImage, PrismicNextLink } from '@prismicio/next';

/**
 * Props for `AboutHighlight`.
 */
export type AboutHighlightProps =
  SliceComponentProps<Content.AboutHighlightSlice>;

/**
 * Component for "AboutHighlight" Slices.
 *
 * Features:
 * - Title on the left
 * - Description paragraphs with resource link
 * - Two images stacked on the right
 * - Footer with copyright
 * - Optional logos section (withLogos variation)
 */
const AboutHighlight: FC<AboutHighlightProps> = ({ slice }) => {
  const { title, description, resource_link, images } = slice.primary;

  const isLogosVariation =
    slice.variation === 'withLogos' || slice.variation === 'logosAndImgBg';
  const isImgBgVariation = slice.variation === 'logosAndImgBg';

  // Get logos fields if using withLogos or logosAndImgBg variation
  const logoSectionTitle =
    isLogosVariation && 'logo_section_title' in slice.primary
      ? slice.primary.logo_section_title
      : null;
  const logos =
    isLogosVariation && 'logos' in slice.primary ? slice.primary.logos : null;

  const backgroundImage =
    isImgBgVariation && 'background_image' in slice.primary
      ? slice.primary.background_image
      : null;
  const blueCta =
    isImgBgVariation && 'blue_cta' in slice.primary
      ? slice.primary.blue_cta
      : false;

  const contentRef = useRef<HTMLDivElement>(null);
  const imagesRef = useRef<HTMLDivElement>(null);
  const logosRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -10% 0px',
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in');
          entry.target.classList.remove('opacity-0', 'translate-y-8');
        }
      });
    }, observerOptions);

    const elementsToAnimate = [
      { ref: contentRef, delay: '0ms' },
      { ref: imagesRef, delay: '200ms' },
      { ref: logosRef, delay: '300ms' },
    ];

    elementsToAnimate.forEach(({ ref, delay }) => {
      if (ref.current) {
        ref.current.classList.add(
          'opacity-0',
          'translate-y-8',
          'transition-all',
          'duration-700',
          'ease-out',
        );
        ref.current.style.transitionDelay = delay;
        observer.observe(ref.current);
      }
    });

    return () => {
      elementsToAnimate.forEach(({ ref }) => {
        if (ref.current) {
          observer.unobserve(ref.current);
        }
      });
    };
  }, []);

  const currentYear = new Date().getFullYear();

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="relative pb-16"
      style={
        isImgBgVariation
          ? undefined
          : {
              background:
                'linear-gradient(0deg, rgba(0, 0, 0, 0.20) 0%, rgba(0, 0, 0, 0.20) 100%), linear-gradient(0deg, rgba(0, 0, 0, 0.20) 0%, rgba(0, 0, 0, 0.20) 100%), linear-gradient(0deg, rgba(0, 0, 0, 0.20) 0%, rgba(0, 0, 0, 0.20) 100%), linear-gradient(0deg, rgba(0, 0, 0, 0.20) 0%, rgba(0, 0, 0, 0.20) 100%), linear-gradient(0deg, rgba(0, 0, 0, 0.20) 0%, rgba(0, 0, 0, 0.20) 100%), #0C3248',
            }
      }
    >
      {/* Background image for logosAndImgBg variation */}
      {isImgBgVariation && isFilled.image(backgroundImage) && (
        <PrismicNextImage
          field={backgroundImage}
          fill
          className="absolute inset-0 h-full w-full object-cover"
        />
      )}
      {/* Main Content Area */}
      <div className="relative z-[1] py-16 md:py-20 lg:py-28 px-4 md:px-8 lg:px-[150px]">
        <div className="max-w-[1140px] mx-auto">
          <div className="flex flex-col lg:flex-row gap-10 lg:gap-[60px] items-center">
            {/* Left Column - Content */}
            <div
              ref={contentRef}
              className="flex flex-col gap-6 w-full lg:w-[520px]"
            >
              {/* Title */}
              {isFilled.richText(title) && (
                <div
                  className={`text-[32px] md:text-[42px] font-medium leading-[1.2] ${isImgBgVariation ? 'text-black' : 'text-white'}`}
                >
                  <PrismicRichText
                    field={title}
                    components={{
                      heading2: ({ children }) => <h2>{children}</h2>,
                    }}
                  />
                </div>
              )}

              {/* Description */}
              {isFilled.richText(description) && (
                <div
                  className={`text-base md:text-lg font-normal leading-[1.5] ${isImgBgVariation ? 'text-black' : 'text-white'}`}
                >
                  <PrismicRichText
                    field={description}
                    components={{
                      hyperlink: ({ children, node }) => (
                        <a
                          href={node.data.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#05f273] underline hover:text-[#03d466] transition-colors"
                        >
                          {children}
                        </a>
                      ),
                    }}
                  />
                </div>
              )}

              {/* Resource Link */}
              {isFilled.link(resource_link) &&
                (isImgBgVariation ? (
                  <div>
                    <PrismicNextLink
                      field={resource_link}
                      className={`inline-block rounded-[100px] border-t border-[#EAEAEA] px-8 py-3 text-base font-semibold text-white transition-opacity hover:opacity-90 ${
                        blueCta ? 'bg-[#4B0FEA]' : 'bg-[#03BF9E]'
                      }`}
                    >
                      {resource_link.text ||
                        'Explore more resources at devsu.com'}
                    </PrismicNextLink>
                  </div>
                ) : (
                  <p className="text-base md:text-lg font-normal leading-[1.5] text-white">
                    Explore more resources at{' '}
                    <PrismicNextLink
                      field={resource_link}
                      className="text-[#05f273] underline hover:text-[#03d466] transition-colors"
                    >
                      {resource_link.text || 'www.devsu.com'}
                    </PrismicNextLink>
                  </p>
                ))}

              {/* Logos Section (withLogos & logosAndImgBg variations) */}
              {isLogosVariation && logos && logos.length > 0 && (
                <div
                  ref={logosRef}
                  className="flex flex-col gap-6 items-start justify-center py-8 w-full"
                >
                  {logoSectionTitle && (
                    <p className="text-xl font-bold leading-[1.4] text-white">
                      {logoSectionTitle}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-6 items-center justify-between w-full">
                    {logos.map((item, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-center"
                      >
                        {isFilled.image(item.white_logo) && (
                          <PrismicNextImage
                            field={item.white_logo}
                            className="h-5 md:h-6 w-auto object-contain opacity-90"
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Right Column - Images */}
            {images && images.length > 0 && (
              <div
                ref={imagesRef}
                className="flex flex-col gap-6 w-full lg:w-[460px]"
              >
                {images.map((item, index) => (
                  <div
                    key={index}
                    className="w-full max-w-[410px] mx-auto rounded-lg overflow-hidden"
                  >
                    {isFilled.image(item.image) && (
                      <PrismicNextImage
                        field={item.image}
                        className="w-full h-auto object-cover rounded-lg"
                      />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer - Copyright */}
      <div className="absolute bottom-0 left-0 right-0 z-10 bg-[#0a0a0a] py-4 w-full">
        <p className="text-lg font-normal leading-[1.5] text-[#666] text-center">
          © {currentYear} Devsu All rights reserved.
        </p>
      </div>
    </section>
  );
};

export default AboutHighlight;
